import { uploadApis } from "@payping/api";
import { InfoCircleFilled, UploadOutlined } from "@payping/icons";
import { ValidNationalId } from "@payping/utils/validation";
import { Col, Input, LegacyForm, Radio, Row, Space, Typography, Upload } from "@payping/web-components";
import type { UploadChangeParam, UploadFile } from "@payping/web-components/components/upload/types";
import React, { ReactElement } from "react";
import { useAppTranslation } from "@/utils/useAppTranslation";
import styles from "@/assets/scss/dashCustomer/form.module.scss";

interface BaseFormProps {
  phoneHelp?: string;
  emailHelp?: string;
  loading?: boolean;
  fileList: UploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
}

const BaseForm: React.FC<BaseFormProps> = (props: BaseFormProps) => {
  const { emailHelp, loading, phoneHelp, fileList, setFileList } = props;
  const { t } = useAppTranslation();

  const uploadButton = (
    <div>
      <UploadOutlined width={"14px"} className={"color--primary"} />
      <div className={"ant-upload-text"}>{t("common.uploadPhoto")}</div>
    </div>
  );

  return (
    <>
      <LegacyForm.Item name={"isBusiness"} label={t("customer.typeTitle")} className={styles.customerType}>
        <Radio.Group buttonStyle={"solid"}>
          <Radio.Button value={false}>{t("customer.type.natural")}</Radio.Button>
          <Radio.Button value={true}>{t("customer.type.legal")}</Radio.Button>
        </Radio.Group>
      </LegacyForm.Item>
      <LegacyForm.Item
        noStyle
        shouldUpdate={(prevValues, currentValues): boolean => prevValues.isBusiness !== currentValues.isBusiness}
      >
        {(form1): ReactElement => (
          <Row gutter={[16, 8]}>
            {form1.getFieldValue("isBusiness") === false ? (
              <>
                <Col xs={24} lg={12}>
                  <LegacyForm.Item label={t("firstName")} name={"firstName"}>
                    <Input disabled={loading} />
                  </LegacyForm.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <LegacyForm.Item label={t("lastName")} name={"lastName"}>
                    <Input disabled={loading} />
                  </LegacyForm.Item>
                </Col>
              </>
            ) : null}
          </Row>
        )}
      </LegacyForm.Item>
      <LegacyForm.Item
        noStyle
        shouldUpdate={(prevValues, currentValues): boolean => prevValues.isBusiness !== currentValues.isBusiness}
      >
        {({ getFieldValue }) =>
          getFieldValue("isBusiness") ? (
            <Row gutter={[16, 8]}>
              <Col xs={24} lg={12}>
                <LegacyForm.Item label={t("businessName")} name={"businessName"}>
                  <Input disabled={loading} />
                </LegacyForm.Item>
              </Col>
              <Col xs={24} lg={7}>
                <LegacyForm.Item label={t("shenaseMelli")} name={"bNationalCode"}>
                  <Input disabled={loading} />
                </LegacyForm.Item>
              </Col>
              <Col xs={24} lg={7}>
                <LegacyForm.Item label={t("common.economyCode")} name={"economicCode"}>
                  <Input disabled={loading} />
                </LegacyForm.Item>
              </Col>
              <Col xs={24} lg={7}>
                <LegacyForm.Item label={t("common.operation.sabtNumber")} name={"sabtNumber"}>
                  <Input disabled={loading} />
                </LegacyForm.Item>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col xs={24} lg={7}>
                <LegacyForm.Item
                  label={t("nationalId")}
                  name={"nationalId"}
                  rules={[
                    {
                      validator: (_rule, value): Promise<void> =>
                        value && !ValidNationalId(value) ? Promise.reject(t("error.nationalId")) : Promise.resolve(),
                    },
                  ]}
                >
                  <Input disabled={loading} />
                </LegacyForm.Item>
              </Col>
            </Row>
          )
        }
      </LegacyForm.Item>
      <Row gutter={[16, 8]}>
        <Col span={24}>
          <Space className={"tw:mb-4!"}>
            <InfoCircleFilled width={10} className={"color--cta"} />
            <Typography.Text>{t("error.onlyPhoneOrEmailRequired")}</Typography.Text>
          </Space>
        </Col>
        <Col xs={24} lg={7}>
          <LegacyForm.Item
            required
            label={t("product.form.phone.mobile")}
            name={"phone"}
            validateStatus={phoneHelp ? "error" : "success"}
            help={phoneHelp}
          >
            <Input disabled={loading} />
          </LegacyForm.Item>
        </Col>
        <Col xs={24} lg={12}>
          <LegacyForm.Item
            label={t("email")}
            name={"email"}
            validateStatus={emailHelp ? "error" : "success"}
            help={emailHelp}
          >
            <Input disabled={loading} />
          </LegacyForm.Item>
        </Col>
      </Row>

      <Space orientation={"vertical"}>
        <Typography.Text>{t("customer.form.upload.placeholder")}</Typography.Text>
        <LegacyForm.Item name={"avatar"}>
          <Upload
            disabled={loading}
            listType={"picture-card"}
            requestFunction={uploadApis.customerPicWithFilePost}
            onChange={(info: UploadChangeParam): void => setFileList(info.fileList)}
            fileList={fileList}
            showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
            allowedExtensions={["jpg", "jpeg", "png"]}
            showTooltip
          >
            {fileList.length ? null : uploadButton}
          </Upload>
        </LegacyForm.Item>
      </Space>
    </>
  );
};

export default BaseForm;
